import { zodResolver } from '@hookform/resolvers/zod'
import FbButton from 'components/FbUI/FbButton'
import { TextInput } from 'components/FormUtils/TextInput'
import Select from 'components/FormUtils/Select'
import { Modal } from 'components/UI/Modal/Modal'
import { useAuth } from 'context/authentication/useAuth'
import { useRSMEligibleUsers } from 'hooks/useRSMEligibleUsers'
import { CompanyEmailExtension } from 'models/companies'
import { User, USER_TYPE_LIMITED, USER_TYPE_STANDARD } from 'models/user'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { useListUserTypes, useInviteUser } from 'services/users'
import { z } from 'zod'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

export interface InviteUserModalProps {
  existingUsers: User[]
  triggerClassName?: string
  triggerIcon?: React.ReactNode
  triggerLabel?: string
}

const createEmailSchema = (
  extensions: CompanyEmailExtension[] = [],
  users: User[]
) =>
  z.object({
    email: z
      .string()
      .email()
      .refine(
        (email) =>
          extensions.find((extension) =>
            email.endsWith(extension.email_extension)
          ),
        {
          message:
            'Email address is not approved for First Bite access. Please reach out to your account manager.',
        }
      )
      .refine((email) => !users.find((u) => u.email === email), {
        message: 'User is already part of your team',
      }),
    user_type: z.string().optional(),
    rsm_assignment: z.number().nullable().optional(),
  })

export function InviteUserModal({
  existingUsers = [],
  triggerClassName,
  triggerIcon,
  triggerLabel = 'Invite User',
}: InviteUserModalProps) {
  const api = useApiService()
  const { isAdmin, isLimited, user } = useAuth()
  const { enableUserTypes } = useFeatureFlagService()
  const { data: companyDomains } = api.useGetCompanyEmailExtensions()
  const [showModal, setShowModal] = useState<boolean>(false)

  // Admin: fetch all user types. Standard: only "Limited" is available.
  const canInvite = !isLimited
  // The /users/user-types/ endpoint requires CanManageUserTypes (Admin-only),
  // so the query must be gated on isAdmin. Standard users still see "Limited"
  // as their only invite option via the availableUserTypes fallback below.
  const { data: userTypes } = useListUserTypes({
    query: { enabled: enableUserTypes && isAdmin },
  })

  const availableUserTypes = useMemo(() => {
    if (isAdmin) return userTypes ?? []
    // Standard users can only invite Limited users. The user-types endpoint
    // is Admin-only, so synthesize the single allowed option locally instead
    // of fetching the list.
    return [{ label: USER_TYPE_LIMITED, value: USER_TYPE_LIMITED }]
  }, [userTypes, isAdmin])

  const formSchema = useMemo(
    () => createEmailSchema(companyDomains, existingUsers),
    [companyDomains, existingUsers]
  )

  type FormSchema = z.infer<typeof formSchema>

  const defaultUserType = isAdmin ? USER_TYPE_STANDARD : USER_TYPE_LIMITED

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    watch,
  } = useForm<FormSchema>({
    defaultValues: {
      email: '',
      user_type: defaultUserType,
      rsm_assignment: isAdmin ? null : (user?.id ?? null),
    },
    resolver: zodResolver(formSchema),
  })

  const selectedType = watch('user_type')
  const selectedRSM = watch('rsm_assignment')
  const isLimitedType = selectedType === USER_TYPE_LIMITED

  const { rsmOptions: adminRsmOptions } = useRSMEligibleUsers(
    enableUserTypes && isAdmin && isLimitedType
  )

  // Standard users: single-option list with themselves (hook not fetched)
  // Admin users: full list from hook (which already injects current user if missing)
  const rsmOptions = useMemo(() => {
    if (isAdmin) return adminRsmOptions
    if (!user) return []
    return [{ label: `${user.first_name} ${user.last_name}`, value: user.id }]
  }, [isAdmin, adminRsmOptions, user])

  const inviteUser = useInviteUser()

  const blockAccept = isLimitedType && !selectedRSM

  function openModal() {
    reset({
      email: '',
      user_type: defaultUserType,
      rsm_assignment: isAdmin ? null : (user?.id ?? null),
    })
    setShowModal(true)
  }

  function hideModal() {
    setShowModal(false)
    reset()
  }

  const onSubmit = handleSubmit(async (values) => {
    await inviteUser.mutateAsync({
      data: {
        email: values.email,
        user_type: enableUserTypes ? values.user_type : undefined,
        rsm_assignment_id:
          enableUserTypes && isLimitedType ? values.rsm_assignment : undefined,
      },
    })
    toast.success('Invite sent successfully!')
    hideModal()
  })

  const inviteUserButtonDisabled = useMemo(() => {
    if (enableUserTypes) {
      return isLimited || availableUserTypes.length === 0
    }
    return false
  }, [enableUserTypes, isLimited, availableUserTypes])

  return (
    <>
      <FbButton
        disabled={inviteUserButtonDisabled}
        onClick={() => openModal()}
        className={triggerClassName}
      >
        {triggerIcon}
        {triggerLabel}
      </FbButton>
      <Modal
        open={showModal}
        title={'Invite User'}
        description={
          "Enter your teammate's work email to invite them to join you on First Bite."
        }
        onOpenChange={(open) => !open && hideModal()}
        loading={isSubmitting}
        blockAccept={blockAccept}
        onAccept={onSubmit}
        acceptButtonText={'Invite'}
      >
        <TextInput
          name="email"
          control={control}
          type="text"
          label={'Work Email'}
          placeholder="name@company.com"
        />

        {enableUserTypes && canInvite && (
          <>
            <Select
              control={control}
              name="user_type"
              label="User Type"
              options={availableUserTypes}
              isClearable={false}
              disabled={!isAdmin}
            />

            {isLimitedType && (
              <Select
                control={control}
                name="rsm_assignment"
                label="Assigned Sample Reviewer"
                options={rsmOptions}
                isClearable={false}
                disabled={!isAdmin}
                placeholder="Select a sample reviewer..."
              />
            )}
          </>
        )}
      </Modal>
    </>
  )
}
